/**
 * Section detail — one page per division of the printed hymnal (~32 routes).
 *
 * THIS IS THE PAGE THE PROJECT WAS MISSING. "Advent hymns Hymnal 1982" is a query
 * church musicians actually type, and until now the site had nothing to answer it
 * with: the index listed all 720 first lines alphabetically, so the only way to
 * find the Advent hymns was to already know they are 53-76.
 *
 * Unlike tune/person/meter pages, sections are NOT threshold-gated — a section is
 * a named division of the book, not an incidental axis, so even Ordination (one
 * hymn) earns its page. See `SectionPage` in `hymnal-pages.ts`.
 *
 * NO LYRICS AND NO NOTATION (plan D7), same as every other page here.
 */
import { Link, useParams } from 'react-router-dom'

import { HYMNAL_1982, index } from '@/data/hymnal'
import { creditedAcross } from '@/lib/hymnal'
import { Crumb, HymnList, SectionTitle } from '@/components/HymnList'
import { NotFound } from '@/components/NotFound'
import { PageHead } from '@/components/PageHead'

const hymnalPath = (rest: string) => `/hymnal/${HYMNAL_1982.id}${rest}`

export default function SectionPage() {
  const { slug } = useParams<{ slug: string }>()
  const section = slug ? index.sectionBySlug.get(slug) : undefined

  if (!section) {
    return (
      <NotFound
        title="No such section"
        detail={`The Hymnal 1982 has no section named "${slug ?? '(none)'}".`}
        backTo={hymnalPath('')}
        backLabel="Browse The Hymnal 1982"
      />
    )
  }

  const hymns = section.hymns
    .map((ref) => index.hymnBySlug.get(ref.slug))
    .filter((h): h is NonNullable<typeof h> => Boolean(h))

  // Meters present in this section, commonest first — the substitution question
  // asked within a season ("we need another Advent text in CM").
  const meters = [
    ...hymns
      .reduce((acc, h) => {
        if (!h.meter || !h.meterSlug) return acc
        const row = acc.get(h.meterSlug) ?? { slug: h.meterSlug, meter: h.meter, count: 0 }
        row.count += 1
        return acc.set(h.meterSlug, row)
      }, new Map<string, { slug: string; meter: string; count: number }>())
      .values(),
  ]
    .sort((a, b) => b.count - a.count || a.meter.localeCompare(b.meter))
    .slice(0, 10)

  // People most credited here. `creditedAcross` resolves each raw credit string
  // through the variant index, so one person spelled two ways counts once and
  // only people who have a page come back — the row can be linked unguarded.
  const people = [...creditedAcross(index, hymns).values()]
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    .slice(0, 12)

  const spine = index.pages.sections
  const at = spine.findIndex((s) => s.slug === section.slug)
  const prev = at > 0 ? spine[at - 1] : undefined
  const next = at > -1 && at < spine.length - 1 ? spine[at + 1] : undefined

  const description =
    `The ${section.label} hymns of ${HYMNAL_1982.label} — ` +
    `${section.hymns.length} hymns, numbers ${section.from}–${section.to}, with tunes, meters and credits.`

  return (
    <article className="mx-auto max-w-3xl px-5 py-12">
      <PageHead
        title={`${section.label} — hymns ${section.from}–${section.to}`}
        description={description}
        path={hymnalPath(`/section/${section.slug}`)}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${section.label} — ${HYMNAL_1982.label}`,
          isPartOf: { '@type': 'WebSite', name: HYMNAL_1982.label },
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: section.hymns.length,
            itemListElement: section.hymns.slice(0, 25).map((h, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: h.title,
              url: `https://fuguelab.com${hymnalPath(`/hymn/${h.slug}`)}`,
            })),
          },
        }}
      />
      <Crumb label={section.label} />

      <header className="mb-8">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
          {section.group ?? 'Section'}
          {section.season ? ' · season' : ''}
        </p>
        <h1 className="text-3xl sm:text-4xl font-display text-foreground mb-3">{section.label}</h1>
        <p className="text-muted-foreground max-w-2xl">
          Hymns {section.from}–{section.to} of {HYMNAL_1982.label} — {section.hymns.length} in all.
          {section.season
            ? ` Everything the hymnal appoints for ${section.season}, in the order it prints them.`
            : ' Listed in the order the hymnal prints them.'}
        </p>
      </header>

      {meters.length > 0 && (
        <section className="mb-8">
          <SectionTitle>Metres in this section</SectionTitle>
          <ul className="flex flex-wrap gap-2">
            {meters.map((m) =>
              index.meterBySlug.has(m.slug) ? (
                <li key={m.slug}>
                  <Link
                    to={hymnalPath(`/meter/${m.slug}`)}
                    className="inline-block rounded border border-border px-2.5 py-1 text-xs text-muted-foreground hover:border-border-strong hover:text-foreground"
                  >
                    {m.meter} <span className="text-muted-foreground">{m.count}</span>
                  </Link>
                </li>
              ) : (
                <li
                  key={m.slug}
                  className="inline-block rounded border border-border px-2.5 py-1 text-xs text-muted-foreground"
                >
                  {m.meter} <span className="text-muted-foreground">{m.count}</span>
                </li>
              )
            )}
          </ul>
        </section>
      )}

      {people.length > 0 && (
        <section className="mb-8">
          <SectionTitle>Most credited here</SectionTitle>
          <ul className="flex flex-wrap gap-x-4 gap-y-1.5">
            {people.map((p) => (
              <li key={p.slug} className="text-sm">
                <Link
                  to={hymnalPath(`/person/${p.slug}`)}
                  className="text-muted-foreground underline decoration-border underline-offset-4 hover:text-foreground hover:decoration-foreground"
                >
                  {p.name}
                </Link>{' '}
                <span className="text-xs text-muted-foreground">{p.count}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <SectionTitle>
          Hymns {section.from}–{section.to}
        </SectionTitle>
        <HymnList hymns={section.hymns} />
      </section>

      {/* The book is a sequence, so the neighbouring sections are the natural next
          step — and they keep every section page linked to two others rather than
          hanging off the index alone. */}
      {(prev || next) && (
        <nav className="mt-12 flex justify-between gap-4 border-t border-border pt-5 text-sm" aria-label="Sections">
          {prev ? (
            <Link
              to={hymnalPath(`/section/${prev.slug}`)}
              className="text-muted-foreground hover:text-foreground min-h-11 flex items-center"
            >
              ← {prev.label}
            </Link>
          ) : (
            <span />
          )}
          {next ? (
            <Link
              to={hymnalPath(`/section/${next.slug}`)}
              className="text-muted-foreground hover:text-foreground min-h-11 flex items-center text-right"
            >
              {next.label} →
            </Link>
          ) : (
            <span />
          )}
        </nav>
      )}
    </article>
  )
}
